import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { validateUrl } from 'webapp/utils/validate';

import safeStyle from 'webapp/helpers/safe-style';
import didUpdate from 'webapp/modifiers/did-update';
import CommonImageUploadDropComponent from 'webapp/components/common/image-upload/drop';
import FormCheckboxSwitchComponent from 'webapp/components/form/checkbox-switch';
import FormInputComponent from 'webapp/components/form/input';

import type Block from 'webapp/models-custom/block';
import type BlockSetup from 'webapp/models-custom/block-setup';
import type VisualizationImage from 'webapp/models-custom/visualization/image';

interface Args {
	blockM: Block;
}

export default class BlockSettingsImageComponent extends Component<Args> {
	@tracked imageUrl = ''; // already saved url
	@tracked externalUrl = '';
	@tracked isOpenOnClickOn = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.setFromModel();
	}

	get block(): Block {
		return this.args.blockM;
	}

	get blockSetup(): BlockSetup {
		return this.block.blockSetup;
	}

	get visualization(): VisualizationImage {
		return this.blockSetup.visualization as VisualizationImage;
	}

	get bgImage(): string {
		return `url("${this.block.imageUrl || this.block.imageOriginalUrl}")`;
	}

	@action
	setFromModel(): void {
		this.isOpenOnClickOn = !!this.block.externalUrl;
		this.externalUrl = this.block.externalUrl || 'https://';
		this.imageUrl = this.block.imageUrl;
	}

	@action
	changeOpenOnClick(isOpenOnClickOn: boolean): void {
		this.isOpenOnClickOn = isOpenOnClickOn;
	}

	@action
	onExternalUrlInputBlur(): void {
		const block = this.block;
		if (!block) {
			return;
		}
		let mustSave = false;
		if (this.isOpenOnClickOn) {
			if (validateUrl(this.externalUrl)) {
				block.externalUrl = this.externalUrl;
				mustSave = true;
			}
		} else {
			block.externalUrl = '';
			mustSave = true;
		}
		if (mustSave) {
			block.blockSetup.saveBlock();
		}
	}

	@action
	onImageAdd(blobUrl: string) {
		this.visualization.openImageEdit(blobUrl);
	}

	@action
	editImage() {
		// editing current image
		this.visualization.openImageEdit('', this.block.imageOriginalUrl, this.block.imageCrop);
	}

	@action
	deleteImage() {
		this.visualization.updateImages('', '', null);
	}
	<template>
		<div class="row row--image-upload" {{didUpdate this.setFromModel @blockM}}>

			{{#if this.visualization.shownImage}}
				<div class="imagedata">
					<div
						class="img"
						style={{safeStyle this.bgImage property="background-image"}}
					></div>
					<span>{{this.visualization.filenameEnd}}</span>
					<a role="button" class="edit" {{on "click" this.editImage}}>Edit image</a>
					<a role="button" class="delete" {{on "click" this.deleteImage}}>Delete</a>
				</div>
			{{else}}
				<CommonImageUploadDropComponent @onImageAdd={{this.onImageAdd}} @theme="dark" />
			{{/if}}

		</div>

		{{#if this.visualization.shownImage}}

			<div class="row row--toggle">
				<FormCheckboxSwitchComponent
					@name="isOpenOnClickOn"
					@selected={{this.isOpenOnClickOn}}
					@onChange={{this.changeOpenOnClick}}
				/>
				<label>Open on click</label>
			</div>

			{{#if this.isOpenOnClickOn}}
				<div class="row">
					<label>External URL</label>
					<FormInputComponent
						maxlength="256"
						@loseFocusOnEnter={{true}}
						@value={{this.externalUrl}}
						@focusOut={{this.onExternalUrlInputBlur}}
						@theme="dark"
					/>
				</div>
			{{/if}}

		{{/if}}
	</template>
}
